import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useAsyncFn } from 'react-use';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import { bePutStylistStripeAccount } from 'actions/stylist/bePutStylistStripeAccount.action';
import IMG_EMPLOYED from 'assets/images/stripe/employed.png';
import IMG_SELF from 'assets/images/stripe/self-employed.png';
import IMG_COMMISSIONS from 'assets/images/stripe/split-commissions.png';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import CheckboxInput from 'components/form/CheckboxInput.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Tooltip from 'components/generic/Tooltip.component';
import UpgradePlanButton from 'components/hr/timeOffSplash/UpgradePlanButton.component';
import DELAY from 'constants/delay.const';
import TRANSFER_POLICY from 'constants/pos/stripeTransferPolicy.const';
import USER_ROLE from 'constants/role';
import S from 'constants/string.const';
import STRIPE_STATUS from 'constants/stripeStatus.const';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectCurrentSalonBilling, selectCurrentSalonEssentialSplitPayments, selectCurrentSalonPricingPlan } from 'selectors/currentSalon.selectors';
import { selectUserRoleFromPin } from 'selectors/simple';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import delayed from 'utils/functions/delayed.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import salonSubscriptionWithSplit from 'utils/salons/salonSubscriptionWithSplit.util';

import { getSalonOnlineSettings } from 'settings/online-booking/actions';

import CN from './SetupSelfEmployedModal.module.scss';

const LINK = 'https://www.getslick.com/legal/terms';
const L = logger('SetupSelfEmployedModal');

const SPLIT_COMMISSIONS_NOT_READY = true;

const AllTransfersButton = ({ selectedOption, handleCheckboxClick, isOptionDisabled }) => {
  const { t } = useTranslation();

  return (
    <div
      onClick={() => handleCheckboxClick(TRANSFER_POLICY.allTransfers)}
      className={cn({
        [CN.checkboxContainer]: true,
        [CN.selected]: selectedOption === TRANSFER_POLICY.allTransfers,
        [CN.disabled]: isOptionDisabled,
      })}
    >
      <CheckboxInput variant="option" checked={selectedOption === TRANSFER_POLICY.allTransfers} disabled={isOptionDisabled}>
        <div className={CN.cbTitle}>{t('onlineBooking.generalSettings.selfEmployedModal.checkbox1Title')}</div>
        <span className={CN.cbText}>{t('onlineBooking.generalSettings.selfEmployedModal.checkbox1Info')}</span>
      </CheckboxInput>
      {selectedOption === TRANSFER_POLICY.allTransfers && (
        <div>
          <img src={IMG_SELF} alt="I am a self-employed sole trader." className={CN.image} />
        </div>
      )}
    </div>
  );
};

const SplitCommissionsButton = forwardRef(({ selectedOption, handleCheckboxClick, isOptionDisabled, imageCommissionRef }, ref) => {
  const { t } = useTranslation();

  return (
    <div
      onClick={() => handleCheckboxClick(TRANSFER_POLICY.splitCommissions)}
      className={cn({
        [CN.checkboxContainer]: true,
        [CN.selected]: selectedOption === TRANSFER_POLICY.splitCommissions,
        [CN.disabled]: isOptionDisabled,
      })}
    >
      <CheckboxInput variant="option" checked={selectedOption === TRANSFER_POLICY.splitCommissions} disabled={isOptionDisabled}>
        <div className={CN.cbTitle}>{t('onlineBooking.generalSettings.selfEmployedModal.checkbox2Title')}</div>
        <span className={CN.cbText}>{t('onlineBooking.generalSettings.selfEmployedModal.checkbox2Info1')}</span>
      </CheckboxInput>
      {selectedOption === TRANSFER_POLICY.splitCommissions && (
        <div ref={imageCommissionRef}>
          <img src={IMG_COMMISSIONS} alt="Commission agreement + any tips taken" className={CN.image} />
          <div className={CN.imageDescription}>{t('onlineBooking.generalSettings.selfEmployedModal.checkbox2Info2')}</div>
        </div>
      )}
    </div>
  );
});

const SetupSelfEmployedModal = ({
  stylistId,
  onClick,
  onClose,
  pricingPlan,
  hasBilling,
  transferPolicy,
  essentialSplitPayments,
  bePutStylistStripeAccount,
  getSalonOnlineSettings,
  determineSetupButtons,
  userRole,
  ncobDepositSettings,
  ...extra
}) => {
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const imageCommissionRef = useRef(null);
  const imageEmployedRef = useRef(null);

  const [selectedOption, setSelectedOption] = useState(transferPolicy || null);
  const [isSelected, setHasAttemptedSelection] = useState(false);

  const isOptionDisabled = salonSubscriptionWithSplit(hasBilling, pricingPlan, essentialSplitPayments);

  const handleCheckboxClick = useCallback(
    (option) => {
      if (selectedOption === option) {
        setSelectedOption(null);
      } else {
        setSelectedOption(option);
      }
      setHasAttemptedSelection(true);
    },
    [selectedOption],
  );

  const handleCheckboxClickAndScroll = useCallback(
    (option) => {
      handleCheckboxClick(option);

      delayed(DELAY.fast, () => {
        if (option === TRANSFER_POLICY.tipOnly && imageEmployedRef.current) {
          imageEmployedRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },
    [handleCheckboxClick],
  );

  const handleSubmit = useCallback(() => {
    if (selectedOption) {
      const transfer_policy = selectedOption;

      onClick(transfer_policy); // POST request
    }
  }, [onClick, selectedOption]);

  const [resultSaveChanges, handleSaveChanges] = useAsyncFn(async () => {
    if (!selectedOption) return;
    const result = await bePutStylistStripeAccount({
      stylistId,
      data: { transfer_policy: selectedOption },
    });
    call('SetupSelfEmployedModal.onClose', onClose);
    L.unignore('handleSaveChanges()', 'getSalonOnlineSettings:', getSalonOnlineSettings(salonId));

    return result.json;
  }, [selectedOption, bePutStylistStripeAccount, stylistId, onClose, getSalonOnlineSettings, salonId]);

  useEffect(() => {
    setSelectedOption(transferPolicy); // update the  transfer_policy
  }, [stylistId, transferPolicy]);

  const isSpinnerRendered = fromAnyToBool(resultSaveChanges.isLoading);
  const isCommissionDisabled = isOptionDisabled;

  return (
    <>
      {isSpinnerRendered && <OverlaySpinner />}
      <SalonModal tag="SetupSelfEmployedModal" {...extra}>
        <SalonModal.Header>
          <div className={CN.modalHeader}>
            <div className={CN.modalTitle}>
              {t('onlineBooking.generalSettings.selfEmployedModal.titlePart1')}
              <span className={CN.highlighted}>{t('onlineBooking.generalSettings.selfEmployedModal.titlePart2')}</span>
            </div>
            <div className={CN.closeButtonContainer}>
              <Button variant="empty" onClick={onClose}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <div className={CN.options}>
              {userRole === USER_ROLE.manager ? ( // userRole from perm token
                <div>
                  {isOptionDisabled ? (
                    <Tooltip variant="top" width="l2" content={`${S.star} ${t('onlineBooking.generalSettings.selfEmployedModal.tooltip1')}`}>
                      <AllTransfersButton selectedOption={selectedOption} handleCheckboxClick={alwaysNoop} isOptionDisabled={isOptionDisabled} />
                    </Tooltip>
                  ) : (
                    <AllTransfersButton selectedOption={selectedOption} handleCheckboxClick={handleCheckboxClickAndScroll} />
                  )}
                  {isCommissionDisabled ? (
                    <Tooltip variant="top" width="l2" content={t('onlineBooking.generalSettings.selfEmployedModal.tooltip2')}>
                      <SplitCommissionsButton selectedOption={selectedOption} handleCheckboxClick={alwaysNoop} isOptionDisabled={isCommissionDisabled} imageCommissionRef={imageCommissionRef} />
                    </Tooltip>
                  ) : (
                    <SplitCommissionsButton selectedOption={selectedOption} handleCheckboxClick={handleCheckboxClickAndScroll} imageCommissionRef={imageCommissionRef} />
                  )}
                </div>
              ) : (
                <div>
                  <Tooltip variant="top" width="l2" content={t('onlineBooking.generalSettings.selfEmployedModal.tooltip3')}>
                    <AllTransfersButton selectedOption={selectedOption} handleCheckboxClick={alwaysNoop} isOptionDisabled={true} />
                    <SplitCommissionsButton selectedOption={selectedOption} handleCheckboxClick={alwaysNoop} isOptionDisabled={SPLIT_COMMISSIONS_NOT_READY} imageCommissionRef={imageCommissionRef} />
                  </Tooltip>
                </div>
              )}

              <div
                onClick={() => handleCheckboxClickAndScroll(TRANSFER_POLICY.tipOnly)}
                className={cn({
                  [CN.checkboxContainer]: true,
                  [CN.selected]: selectedOption === TRANSFER_POLICY.tipOnly,
                })}
              >
                <CheckboxInput variant="option" checked={selectedOption === TRANSFER_POLICY.tipOnly}>
                  <div className={CN.cbTitle}>{t('onlineBooking.generalSettings.selfEmployedModal.checkbox3Title')}</div>
                  <span className={CN.cbText}>{t('onlineBooking.generalSettings.selfEmployedModal.checkbox3Info')}</span>
                </CheckboxInput>
                {selectedOption === TRANSFER_POLICY.tipOnly && (
                  <div ref={imageEmployedRef}>
                    <img src={IMG_EMPLOYED} alt="I am employed" className={CN.image} />
                  </div>
                )}
              </div>
            </div>
            {isSelected && !selectedOption && <div className={CN.errorMessage}>{t('onlineBooking.generalSettings.selfEmployedModal.errorMessage')}</div>}
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.footer}>
            <div className={CN.footerTandC}>
              {t('onlineBooking.generalSettings.selfEmployedModal.termsAndCondPart1')}
              <span className={CN.focused}>
                {S.quoteOpen}
                {determineSetupButtons === STRIPE_STATUS.complete || determineSetupButtons === STRIPE_STATUS.restricted
                  ? t('onlineBooking.generalSettings.selfEmployedModal.saveChanges')
                  : t('onlineBooking.generalSettings.selfEmployedModal.acceptAndCont')}
                {S.quoteClose}
              </span>
              {t('onlineBooking.generalSettings.selfEmployedModal.termsAndCondPart2')}{' '}
              <ExternalLink link={LINK} variant="inline">
                <span className={CN.link}>{t('onlineBooking.generalSettings.selfEmployedModal.termsAndCondPart3')}</span>
              </ExternalLink>
            </div>
            <div className={CN.innerFooterContainer}>
              <div>
                {isOptionDisabled && (
                  <UpgradePlanButton variantWhite>
                    <span className={CN.buttonSpace}>{t('onlineBooking.generalSettings.selfEmployedModal.upgadePlanButton')}</span>
                  </UpgradePlanButton>
                )}
              </div>

              {determineSetupButtons === STRIPE_STATUS.complete || determineSetupButtons === STRIPE_STATUS.restricted ? (
                <Button variant="primaryForce" onClick={handleSaveChanges} disabled={!selectedOption}>
                  {t('onlineBooking.generalSettings.selfEmployedModal.saveChanges').toUpperCase()}
                </Button>
              ) : (
                <Button variant="primaryForce" onClick={handleSubmit} disabled={!selectedOption}>
                  <span className={CN.buttonSpace}>{t('onlineBooking.generalSettings.selfEmployedModal.acceptAndCont').toUpperCase()}</span>
                </Button>
              )}
            </div>
          </div>
        </SalonModal.Footer>
      </SalonModal>
    </>
  );
};

const mapStateToProps = (state) => ({
  hasBilling: selectCurrentSalonBilling(state),
  pricingPlan: selectCurrentSalonPricingPlan(state),
  essentialSplitPayments: selectCurrentSalonEssentialSplitPayments(state),
  userRole: selectUserRoleFromPin(state),
  ncobDepositSettings: state?.currentSalon?.ncob_deposit_settings,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      bePutStylistStripeAccount,
      getSalonOnlineSettings,
    },
    dispatch,
  );

const SetupSelfEmployedModalHoc = connect(mapStateToProps, mapDispatchToProps)(SetupSelfEmployedModal);

export default SetupSelfEmployedModalHoc;
